import React, { useRef, useEffect } from "react";
import classnames from "classnames";
import { Intent, Icon, FormGroup, Classes } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import css from "../form.module.scss";
import SignatureCanvas from "react-signature-canvas"
import { ISignatureFieldProps } from "../models";
import { FastField, FieldProps, getIn } from "formik";
import { validationMethodFactory, hasRequiredValidation, findRequiredValidation } from "../models/validation";
import RequiredIndicator from "../../../shared/RequiredIndicator";
import useFormContext from "../FormContext";


const canvasProps: React.CanvasHTMLAttributes<HTMLCanvasElement> = {
  // Note: Because of the way `react-signature-canvas` code works,
  // we must specify `height` and `width` as explicit properties,
  // rather than via `style` or `className` attributes.
  width: 400,
  height: 120
}

type  Props<T=any> = FieldProps<T> & { fieldProps: ISignatureFieldProps };

const BoundSignatureField = ({ field, form, fieldProps }: Props) => {
  const { isEditing, isPrinting, isViewing } = useFormContext();
  const sigRef = useRef<SignatureCanvas>(null);
  const error = (form.submitCount || getIn(form.touched, field.name)) && getIn(form.errors, field.name);
  const intent = error ? Intent.DANGER : Intent.NONE;
  const isRequired = hasRequiredValidation(fieldProps.validations);
  const className = classnames(css.formPart, css.signature, error && css.error, isPrinting && css.print, isViewing && css.disabled);
  const disabled = fieldProps.disabled ?? false;

  const onEnd = () => {
    if (sigRef.current) {
      //  const canvas = sigRef.current.getTrimmedCanvas();
      //  const data = canvas.toDataURL();
      const data = sigRef.current.toDataURL();
      form.setFieldValue(field.name, data);
      form.setFieldTouched(field.name);
    }
  }

  useEffect(() => {
    if (sigRef.current && !isEditing) {
      sigRef.current.off();
      if (typeof field.value === "string" && field.value.startsWith("data:image")) {
        sigRef.current.fromDataURL(field.value);
      }
    }
  }, [field.value, isEditing]);

  const clearSignature = () =>  {
    sigRef.current && sigRef.current.clear();
    form.setFieldValue(field.name, undefined);
  }

  const ClearButton = () => !sigRef.current || sigRef.current.isEmpty() || !isEditing
    ? null
    : (<Icon
        htmlTitle="Undo / Clear"
        className={Classes.TEXT_MUTED}
        icon={IconNames.UNDO} // DISABLE, UNDO, KEY_BACKSPACE
        onClick={clearSignature}
        style={{position: 'absolute', left: -18, top: 1}}
      />);

  return (
    <FormGroup
      label={fieldProps.label}
      labelInfo={<RequiredIndicator show={isRequired} isPrinting={isPrinting} />}
      intent={intent}
      helperText={isEditing ? (error || <>&nbsp;</>) : undefined}
      style={fieldProps.style}
      className={className}
      disabled={disabled}
    >
      <div style={{display: 'flex'}}>
        <div>
          <SignatureCanvas
            canvasProps={canvasProps}
            ref={sigRef}
            penColor="blue"
            onEnd={onEnd}
            //clearOnResize={false}
          />
        </div>
        <div style={{position: 'relative'}}>
          <ClearButton />
        </div>
      </div>
    </FormGroup>);
}


const SignatureField: React.FC<ISignatureFieldProps> = (props) => {
  console.assert(props && props.binding);
  // Override default message for required field, unless an explicit one has been provided in the template.
  const requiredValidation = findRequiredValidation(props.validations);
  if (requiredValidation && !requiredValidation.message) {
    requiredValidation.message = "Signature Required";
  }

  const { isEditing } = useFormContext();
  const validate = isEditing ? validationMethodFactory(props) : undefined;
  return <FastField name={props.binding} fieldProps={props} component={BoundSignatureField} validate={validate} />;
}


export default SignatureField;






